// Volume Discount Management
import { useState, useCallback, useMemo } from "react";
import {
  Percent,
  Plus,
  Pencil,
  AlertCircle,
  Layers,
  TrendingDown,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useVolumeDiscounts,
  useCreateVolumeDiscount,
} from "@/hooks/admin/useB2B";
import type {
  VolumeDiscount,
  CreateVolumeDiscountInput,
} from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMPTY_FORM: CreateVolumeDiscountInput = {
  name: "",
  product_id: "",
  min_qty: 1,
  max_qty: null,
  discount_percent: 0,
  is_active: true,
};

// ---------------------------------------------------------------------------
// Tier visualization helper
// ---------------------------------------------------------------------------

function DiscountTierBadge({
  percent,
}: {
  readonly percent: number;
}) {
  const intensity =
    percent >= 20
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : percent >= 10
        ? "bg-amber-100 text-amber-700 border-amber-200"
        : percent >= 5
          ? "bg-blue-100 text-blue-700 border-blue-200"
          : "bg-stone-100 text-stone-600 border-stone-200";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${intensity}`}
    >
      <TrendingDown className="h-3 w-3" />
      -{percent}%
    </span>
  );
}

// ---------------------------------------------------------------------------
// Create form modal
// ---------------------------------------------------------------------------

interface CreateModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (input: CreateVolumeDiscountInput) => Promise<void>;
  readonly isSaving: boolean;
}

function CreateVolumeDiscountModal({
  open,
  onClose,
  onSave,
  isSaving,
}: CreateModalProps) {
  const [form, setForm] = useState<CreateVolumeDiscountInput>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setError(null);
    onClose();
  }, [onClose]);

  const update = useCallback(
    <K extends keyof CreateVolumeDiscountInput>(
      key: K,
      value: CreateVolumeDiscountInput[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) {
      setError("Le nom est requis.");
      return;
    }
    if (!form.product_id.trim()) {
      setError("L'identifiant produit est requis.");
      return;
    }
    if (form.discount_percent <= 0) {
      setError("La remise doit être supérieure à 0%.");
      return;
    }
    if (form.discount_percent > 100) {
      setError("La remise ne peut pas dépasser 100%.");
      return;
    }
    if (form.max_qty !== null && form.max_qty < form.min_qty) {
      setError("La quantité max doit être supérieure à la quantité min.");
      return;
    }
    setError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [form, onSave, handleClose]);

  const inputClass =
    "h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100";
  const labelClass = "mb-1.5 block text-sm font-medium text-stone-700";

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Nouvelle remise volume"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Creer
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className={labelClass}>Nom de la regle</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Ex : Remise palette complete"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Identifiant produit</label>
          <input
            type="text"
            value={form.product_id}
            onChange={(e) => update("product_id", e.target.value)}
            placeholder="ID du produit ou categorie"
            className={inputClass}
          />
          <p className="mt-1 text-xs text-stone-400">
            Laissez vide pour appliquer a tous les produits
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Qte min</label>
            <input
              type="number"
              min={1}
              value={form.min_qty}
              onChange={(e) => update("min_qty", Number(e.target.value))}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Qte max</label>
            <input
              type="number"
              min={1}
              value={form.max_qty ?? ""}
              onChange={(e) =>
                update(
                  "max_qty",
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              placeholder="Illimite"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Remise (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              value={form.discount_percent}
              onChange={(e) =>
                update("discount_percent", Number(e.target.value))
              }
              className={inputClass}
            />
          </div>
        </div>

        {/* Tier preview */}
        {form.discount_percent > 0 && (
          <div className="rounded-lg border border-stone-100 bg-stone-50 px-4 py-3">
            <p className="mb-1 text-xs font-medium text-stone-500">
              Apercu de la regle
            </p>
            <p className="text-sm text-stone-700">
              A partir de{" "}
              <span className="font-semibold">{form.min_qty} unites</span>
              {form.max_qty && (
                <>
                  {" "}
                  jusqu'a{" "}
                  <span className="font-semibold">{form.max_qty} unites</span>
                </>
              )}
              , remise de{" "}
              <span className="font-semibold text-amber-700">
                {form.discount_percent}%
              </span>
            </p>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            id="discount-active"
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor="discount-active"
            className="text-sm font-medium text-stone-700"
          >
            Remise active
          </label>
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function VolumeDiscounts() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string | null>>({
    status: null,
  });

  const { data, isLoading, isError, refetch } = useVolumeDiscounts();
  const createDiscount = useCreateVolumeDiscount();

  const discounts = data?.discounts ?? [];

  // Client-side filtering
  const filteredDiscounts = useMemo(() => {
    let result = [...discounts];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          d.product_name.toLowerCase().includes(q),
      );
    }

    if (filters.status) {
      const isActive = filters.status === "active";
      result = result.filter((d) => d.is_active === isActive);
    }

    return result;
  }, [discounts, search, filters.status]);

  const kpiData = useMemo(() => {
    const total = discounts.length;
    const active = discounts.filter((d) => d.is_active).length;
    const avgDiscount =
      total > 0
        ? discounts.reduce((s, d) => s + d.discount_percent, 0) / total
        : 0;
    const maxDiscount =
      total > 0
        ? Math.max(...discounts.map((d) => d.discount_percent))
        : 0;
    return { total, active, avgDiscount, maxDiscount };
  }, [discounts]);

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
  }, []);

  const handleSave = useCallback(
    async (input: CreateVolumeDiscountInput) => {
      await createDiscount.mutateAsync(input);
      setIsCreateOpen(false);
    },
    [createDiscount],
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: "status",
        label: "Statut",
        type: "select" as const,
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
    ],
    [],
  );

  const columns: readonly ColumnDef<VolumeDiscount>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Regle",
        cell: (d) => (
          <span className="font-medium text-stone-900">{d.name}</span>
        ),
      },
      {
        id: "product_name",
        header: "Produit",
        cell: (d) => (
          <span className="text-sm text-stone-600">{d.product_name}</span>
        ),
      },
      {
        id: "quantity_range",
        header: "Plage de quantites",
        cell: (d) => (
          <div className="flex items-center gap-1.5 text-sm text-stone-600">
            <span className="font-mono text-xs rounded bg-stone-100 px-1.5 py-0.5">
              {d.min_qty}
            </span>
            <span className="text-stone-300">-</span>
            <span className="font-mono text-xs rounded bg-stone-100 px-1.5 py-0.5">
              {d.max_qty ?? "+infini"}
            </span>
            <span className="text-xs text-stone-400">unites</span>
          </div>
        ),
      },
      {
        id: "discount_percent",
        header: "Remise",
        cell: (d) => <DiscountTierBadge percent={d.discount_percent} />,
      },
      {
        id: "is_active",
        header: "Statut",
        cell: (d) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              d.is_active
                ? "bg-emerald-100 text-emerald-700"
                : "bg-stone-100 text-stone-500"
            }`}
          >
            {d.is_active ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: () => (
          <button
            type="button"
            className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            aria-label="Modifier"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Percent className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Remises volume
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement..."
                  : `${discounts.length} regle(s) configuree(s)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouvelle remise volume
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total regles"
            value={kpiData.total}
            format="number"
            icon={<Layers className="h-4 w-4" />}
          />
          <KPICard
            label="Actives"
            value={kpiData.active}
            format="number"
            icon={<Percent className="h-4 w-4" />}
          />
          <KPICard
            label="Remise moyenne"
            value={kpiData.avgDiscount}
            format="percent"
          />
          <KPICard
            label="Remise max"
            value={kpiData.maxDiscount}
            format="percent"
            icon={<TrendingDown className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par nom ou produit..."
          />
          <FilterBar
            filters={filterConfigs}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les remises.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Reessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && filteredDiscounts.length === 0 ? (
              <EmptyState
                icon={<Percent className="h-6 w-6" />}
                title="Aucune remise volume"
                description={
                  search || filters.status
                    ? "Aucune remise ne correspond aux filtres."
                    : "Creez votre premiere remise volume."
                }
                action={
                  !search && !filters.status
                    ? {
                        label: "Nouvelle remise volume",
                        onClick: () => setIsCreateOpen(true),
                      }
                    : undefined
                }
              />
            ) : (
              <AdminDataTable
                data={filteredDiscounts}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      <CreateVolumeDiscountModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSave={handleSave}
        isSaving={createDiscount.isPending}
      />
    </AdminLayout>
  );
}
